import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ArrowLeft, Building2, Loader2, Plus, ShieldCheck, UserPlus } from 'lucide-react';
import {
  PlatformAuditEntry,
  TenantSummary,
  TenantUser,
  createTenant,
  listPlatformAudit,
  listTenantUsers,
  listTenants,
  provisionTenantAdmin,
  setTenantStatus,
  updateTenant,
} from '../admin';

/**
 * Managing customer organisations (DESIGN.md §9.7, D70).
 *
 * Replaces running `python -m app.admin` as a one-off ECS task, which needed AWS credentials,
 * shell access and the VPC. Correct for bootstrapping the platform itself; wrong as the permanent
 * answer, because onboarding a clinic is a business act rather than an infrastructure operation.
 *
 * Two things this screen deliberately does not offer, and both are the point rather than
 * omissions:
 *
 * * **No delete.** Custody is immutable (§9.1) and a tenant owns studies that own signed reports.
 *   Ending a relationship is a status change — reversible, audited, and the data stays
 *   attributable to the organisation that owned it.
 * * **No role picker** when provisioning an administrator. It is always TENANT_ADMIN, which
 *   carries no PHI access (§7.1); the tenant's own admin then creates clinicians inside their
 *   organisation. A role picker here would be a route for a platform operator to mint themselves
 *   a physician account in a customer's tenant.
 *
 * The server enforces both regardless of what this sends.
 */

const STATUS_STYLE: Record<string, string> = {
  ACTIVE: 'bg-emerald-950 border-emerald-800 text-emerald-300',
  SUSPENDED: 'bg-rose-950 border-rose-800 text-rose-300',
  OFFBOARDING: 'bg-amber-950 border-amber-800 text-amber-300',
};

const hours = (seconds: number) =>
  seconds % 3600 === 0 ? `${seconds / 3600} h` : `${Math.round(seconds / 60)} min`;

export const PlatformOps: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [audit, setAudit] = useState<PlatformAuditEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [tenantUsers, setTenantUsers] = useState<Record<string, TenantUser[]>>({});

  const load = useCallback(async () => {
    try {
      const [rows, entries] = await Promise.all([listTenants(), listPlatformAudit()]);
      setTenants(rows);
      setAudit(entries);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load organisations.');
    }
  }, []);
  useEffect(() => void load(), [load]);

  const act = async (fn: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      setError(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That action was refused.');
    } finally {
      setBusy(false);
    }
  };

  const openUsers = async (tenant: TenantSummary) => {
    if (expanded === tenant.id) {
      setExpanded(null);
      return;
    }
    setExpanded(tenant.id);
    if (!tenantUsers[tenant.id]) {
      try {
        const rows = await listTenantUsers(tenant.id);
        setTenantUsers((prev) => ({ ...prev, [tenant.id]: rows }));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load users.');
      }
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-4 px-6 py-8">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <button
          onClick={() => setCreating((v) => !v)}
          className="flex items-center gap-1.5 rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-500"
        >
          <Plus className="h-3.5 w-3.5" /> New organisation
        </button>
      </div>

      <div>
        <h1 className="flex items-center gap-2 text-lg font-bold text-white">
          <Building2 className="h-5 w-5 text-cyan-400" /> Organisations
        </h1>
        <p className="mt-1 text-xs text-slate-400">
          Contract terms and membership. No patient data is reachable from this screen — platform
          operations is cross-tenant but PHI-free (§9.7).
        </p>
      </div>

      {error && (
        <div className="flex items-center justify-between rounded-xl border border-rose-800 bg-rose-950 px-4 py-2 text-xs text-rose-200">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-rose-300">Dismiss</button>
        </div>
      )}

      {creating && <CreateTenantForm busy={busy} onCancel={() => setCreating(false)}
        onCreate={async (body) => { await act(() => createTenant(body)); setCreating(false); }} />}

      <div className="space-y-2">
        {tenants.map((tenant) => (
          <div key={tenant.id} className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-white">{tenant.name}</span>
                  <span className="mono rounded border border-slate-700 px-1.5 py-0.5 text-[10px] text-slate-400">
                    {tenant.slug}
                  </span>
                  <span className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_STYLE[tenant.status]}`}>
                    {tenant.status}
                  </span>
                  {tenant.is_platform_operator && (
                    <span className="flex items-center gap-1 rounded border border-cyan-800 bg-cyan-950 px-1.5 py-0.5 text-[10px] text-cyan-300">
                      <ShieldCheck className="h-3 w-3" /> PLATFORM
                    </span>
                  )}
                </div>
                <p className="mono mt-1 text-[11px] text-slate-500">
                  {tenant.jurisdiction} · {tenant.residency_region} · SLA {tenant.sla_hours} h ·
                  idle {hours(tenant.idle_logout_seconds)} ·{' '}
                  {tenant.separation_of_duties_required ? 'separation of duties' : 'no separation of duties'}
                </p>
                <p className="mono mt-0.5 text-[11px] text-slate-500">
                  {tenant.user_count} user(s) · {tenant.studies_registered.toLocaleString()} studies registered
                </p>
              </div>

              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => void openUsers(tenant)}
                  className="rounded border border-slate-700 px-2 py-1 text-[11px] text-slate-300 hover:border-slate-500"
                >
                  {expanded === tenant.id ? 'Hide users' : 'Users'}
                </button>
                <StatusButton tenant={tenant} busy={busy}
                  onChange={(status, reason) => void act(() => setTenantStatus(tenant.id, status, reason))} />
              </div>
            </div>

            {expanded === tenant.id && (
              <TenantDetail
                tenant={tenant}
                users={tenantUsers[tenant.id]}
                busy={busy}
                onUpdate={(body) => act(() => updateTenant(tenant.id, body))}
                onProvision={async (body) => {
                  await act(() => provisionTenantAdmin(tenant.id, body));
                  const rows = await listTenantUsers(tenant.id);
                  setTenantUsers((prev) => ({ ...prev, [tenant.id]: rows }));
                }}
              />
            )}
          </div>
        ))}
        {tenants.length === 0 && !error && (
          <p className="flex items-center gap-2 text-xs text-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading organisations…
          </p>
        )}
      </div>

      <AuditTrail entries={audit} />
    </div>
  );
};

const CreateTenantForm: React.FC<{
  busy: boolean;
  onCancel: () => void;
  onCreate: (body: { slug: string; name: string; jurisdiction: string; sla_hours: number }) => void;
}> = ({ busy, onCancel, onCreate }) => {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [jurisdiction, setJurisdiction] = useState('IN');
  const [sla, setSla] = useState(48);

  // Suggested from the name, and still editable: a slug is an identifier that appears in support
  // conversations and operator tooling, and it cannot be changed afterwards.
  const suggested = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  return (
    <div className="space-y-3 rounded-xl border border-cyan-900 bg-slate-900/60 p-4">
      <h2 className="text-sm font-bold text-white">New organisation</h2>
      <div className="grid gap-3 md:grid-cols-2">
        <label className="text-xs text-slate-400">
          Name
          <input value={name} onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-white"
            placeholder="Acme Health" />
        </label>
        <label className="text-xs text-slate-400">
          Slug <span className="text-slate-600">(permanent)</span>
          <input value={slug || suggested} onChange={(e) => setSlug(e.target.value)}
            className="mono mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-white"
            placeholder="acme-health" />
        </label>
        <label className="text-xs text-slate-400">
          Jurisdiction
          <input value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value.toUpperCase())}
            maxLength={8}
            className="mono mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-white" />
        </label>
        <label className="text-xs text-slate-400">
          SLA (hours)
          <input type="number" min={1} max={720} value={sla}
            onChange={(e) => setSla(Number(e.target.value))}
            className="mono mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-white" />
        </label>
      </div>
      <div className="flex gap-2">
        <button
          disabled={busy || !name.trim() || !(slug || suggested)}
          onClick={() => onCreate({ slug: slug || suggested, name: name.trim(), jurisdiction, sla_hours: sla })}
          className="rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
        >
          Create
        </button>
        <button onClick={onCancel} className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-slate-300">
          Cancel
        </button>
      </div>
    </div>
  );
};

const StatusButton: React.FC<{
  tenant: TenantSummary;
  busy: boolean;
  onChange: (status: 'ACTIVE' | 'SUSPENDED', reason: string) => void;
}> = ({ tenant, busy, onChange }) => {
  // The platform operator's own tenant is excluded: suspending it revokes the only role that can
  // reverse the suspension. The server refuses it too — this just doesn't offer the trap.
  if (tenant.is_platform_operator) return null;
  const suspending = tenant.status === 'ACTIVE';
  return (
    <button
      disabled={busy}
      onClick={() => {
        const reason = window.prompt(
          suspending
            ? `Suspend ${tenant.name}? Their users lose access immediately.\n\nWhy? (recorded in the audit trail)`
            : `Reactivate ${tenant.name}?\n\nWhy? (recorded in the audit trail)`,
        );
        if (reason && reason.trim()) onChange(suspending ? 'SUSPENDED' : 'ACTIVE', reason.trim());
      }}
      className={`rounded border px-2 py-1 text-[11px] disabled:opacity-40 ${
        suspending
          ? 'border-rose-800 text-rose-300 hover:border-rose-500'
          : 'border-emerald-800 text-emerald-300 hover:border-emerald-500'
      }`}
    >
      {suspending ? 'Suspend' : 'Reactivate'}
    </button>
  );
};

const TenantDetail: React.FC<{
  tenant: TenantSummary;
  users?: TenantUser[];
  busy: boolean;
  onUpdate: (body: Record<string, unknown>) => void;
  onProvision: (body: { email: string; full_name: string; reason: string }) => void;
}> = ({ tenant, users, busy, onUpdate, onProvision }) => {
  const [sla, setSla] = useState(tenant.sla_hours);
  const [idleMinutes, setIdleMinutes] = useState(Math.round(tenant.idle_logout_seconds / 60));
  const [separation, setSeparation] = useState(tenant.separation_of_duties_required);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');

  const changed =
    sla !== tenant.sla_hours ||
    idleMinutes !== Math.round(tenant.idle_logout_seconds / 60) ||
    separation !== tenant.separation_of_duties_required;

  return (
    <div className="mt-4 grid gap-4 border-t border-slate-800 pt-4 md:grid-cols-2">
      <div className="space-y-2">
        <h3 className="text-xs font-bold uppercase text-slate-400">Contract terms</h3>
        <label className="block text-xs text-slate-400">
          SLA (hours)
          <input type="number" min={1} max={720} value={sla} onChange={(e) => setSla(Number(e.target.value))}
            className="mono mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-white" />
        </label>
        <label className="block text-xs text-slate-400">
          Idle logout (minutes)
          <input type="number" min={1} max={1440} value={idleMinutes}
            onChange={(e) => setIdleMinutes(Number(e.target.value))}
            className="mono mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-white" />
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-400">
          <input type="checkbox" checked={separation} onChange={(e) => setSeparation(e.target.checked)} />
          Separation of duties required (one person may not both prepare and sign)
        </label>
        <button
          disabled={busy || !changed}
          onClick={() => onUpdate({
            sla_hours: sla,
            idle_logout_seconds: idleMinutes * 60,
            separation_of_duties_required: separation,
          })}
          className="rounded-lg border border-cyan-800 bg-cyan-950 px-3 py-1.5 text-xs font-semibold text-cyan-300 disabled:opacity-40"
        >
          Save changes
        </button>
      </div>

      <div className="space-y-2">
        <h3 className="text-xs font-bold uppercase text-slate-400">Users</h3>
        <ul className="space-y-1">
          {(users ?? []).map((u) => (
            <li key={u.id} className="flex items-center justify-between gap-2 text-xs text-slate-300">
              <span className="truncate">{u.email}</span>
              <span className="mono shrink-0 text-[10px] text-slate-500">{u.roles.join(' · ')}</span>
            </li>
          ))}
          {users?.length === 0 && (
            <li className="text-xs text-slate-500">
              No users yet — add the first administrator below.
            </li>
          )}
        </ul>

        <div className="space-y-1.5 rounded-lg border border-slate-800 p-2">
          {/* No role picker, deliberately. Always TENANT_ADMIN, which carries no PHI access —
              the tenant's own admin creates clinicians inside their organisation. A picker here
              would let a platform operator grant themselves a physician account in a customer's
              tenant, which is the standing path §9.7 exists to close. */}
          <p className="flex items-start gap-1.5 text-[11px] text-slate-400">
            <UserPlus className="mt-px h-3 w-3 shrink-0 text-cyan-400" />
            Adds a <strong className="text-slate-300">tenant administrator</strong>, who can then
            create clinicians themselves. This cannot grant access to patient data.
          </p>
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@acme.test"
            className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-white" />
          <input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Full name"
            className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-white" />
          <button
            disabled={busy || !email.trim() || !fullName.trim() || tenant.status !== 'ACTIVE'}
            title={tenant.status !== 'ACTIVE' ? `${tenant.status} organisations cannot gain users` : undefined}
            onClick={() => {
              const reason = window.prompt('Why is this administrator being added? (recorded in the audit trail)', 'Onboarding');
              if (reason && reason.trim()) {
                onProvision({ email: email.trim(), full_name: fullName.trim(), reason: reason.trim() });
                setEmail('');
                setFullName('');
              }
            }}
            className="w-full rounded border border-slate-700 px-2 py-1 text-[11px] font-semibold text-slate-300 hover:border-cyan-600 hover:text-cyan-300 disabled:opacity-40"
          >
            Add administrator
          </button>
        </div>
      </div>
    </div>
  );
};

const AuditTrail: React.FC<{ entries: PlatformAuditEntry[] }> = ({ entries }) => (
  <div className="space-y-2">
    <h2 className="flex items-center gap-2 text-sm font-bold text-white">
      <AlertTriangle className="h-4 w-4 text-amber-400" /> Platform audit
    </h2>
    {/* Readable from the product rather than only from the database, because an audit trail
        nobody can see is one nobody checks. Append-only server-side. */}
    <div className="overflow-x-auto rounded-xl border border-slate-800">
      <table className="w-full text-left text-xs">
        <thead className="bg-slate-900/60 text-slate-500">
          <tr>
            <th className="px-3 py-2 font-medium">When</th>
            <th className="px-3 py-2 font-medium">Who</th>
            <th className="px-3 py-2 font-medium">Action</th>
            <th className="px-3 py-2 font-medium">Organisation</th>
            <th className="px-3 py-2 font-medium">Reason</th>
          </tr>
        </thead>
        <tbody className="text-slate-300">
          {entries.map((e) => (
            <tr key={e.id} className="border-t border-slate-800/60">
              <td className="mono whitespace-nowrap px-3 py-1.5 text-slate-500">
                {new Date(e.ts).toLocaleString()}
              </td>
              <td className="px-3 py-1.5">{e.actor_email ?? '—'}</td>
              <td className="mono px-3 py-1.5">{e.action}</td>
              <td className="mono px-3 py-1.5">{e.target_tenant_slug ?? '—'}</td>
              <td className="px-3 py-1.5 text-slate-400">{e.reason ?? '—'}</td>
            </tr>
          ))}
          {entries.length === 0 && (
            <tr><td colSpan={5} className="px-3 py-3 text-slate-500">Nothing recorded yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);
